import { useMemo, useState } from "react";
import {
  Award, Copy, Check, Download, Facebook, Linkedin, Mail, MessageSquare,
  Instagram, Send, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { buildListingShareUrl } from "@/utils/shareUrl";

/**
 * Social Share Section — lives inside the Share & Promote dashboard.
 *
 * Two parts:
 *   1) Verified Badge share card — auto-generated PNG with the business name
 *      baked onto the gold LocalBest badge (server-rendered).
 *   2) Share Whole Listing — one-click web-intent share buttons for FB, X,
 *      LinkedIn, Reddit, Pinterest, WhatsApp, Telegram, Messenger, Email, SMS,
 *      and "Copy caption + download image" for Instagram & TikTok.
 */
export default function SocialShareSection({ listing }) {
  const [copied, setCopied] = useState(null);

  const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
  const slug = listing?.slug;
  const origin = (typeof window !== "undefined" && window.location.origin) || "https://localbest.ai";

  // Canonical human-facing URL — shown to users on the listing page itself.
  const listingUrl = useMemo(() => {
    if (!listing) return origin;
    const citySlug = (listing.city || "local").toLowerCase().replace(/\s+/g, "-");
    const stateSlug = (listing.state || "").toLowerCase();
    const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
    return `https://localbest.ai/directory/${listing.category_id || "business"}/${cityState}/${listing.slug}`;
  }, [listing, origin]);

  // Backend-rendered share URL — used in every share button below so iMessage,
  // Facebook, LinkedIn, Slack, SMS, etc. (which don't run JS) hit a backend
  // route that serves a tiny HTML page with the correct per-listing meta tags
  // (badge image + "{Name} is Verified on LocalBest.ai").
  // Humans clicking the link are auto-redirected to the real listing.
  const shareUrl = useMemo(() => {
    if (!listing) return origin;
    return buildListingShareUrl(listing);
  }, [listing, origin]);

  const bizName = listing?.name || "My Business";

  // CTA-style caption requested by the owner — works as a 3rd-person message
  // (anyone resharing can post this without it sounding like the owner wrote it).
  const caption = `View LocalBest.ai ${bizName}'s Verified Listing for Info, Incentives, and to Help Your Local Business & Community.`;
  const fullPost = `${caption}\n\n${listingUrl}\n\n#LocalBest #BestOfBest #SupportLocal`;

  const ogImageUrl = slug ? `${API_V1}/share/og-badge/${slug}` : null;
  const squareImageUrl = slug ? `${API_V1}/share/og-badge-square/${slug}` : null;
  const ogDownloadUrl = slug ? `${ogImageUrl}?download=1` : null;
  const squareDownloadUrl = slug ? `${squareImageUrl}?download=1` : null;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(null), 1800);
    } catch (_) { toast.error("Copy failed"); }
  };

  // Web-intent share URLs (open the platform's native share composer).
  // We share the BACKEND `shareUrl` so crawlers get correct per-listing meta tags.
  const enc = encodeURIComponent;
  const shareTargets = [
    {
      key: "facebook",
      label: "Facebook",
      color: "#1877F2",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(caption)}`,
    },
    {
      key: "twitter",
      label: "X (Twitter)",
      color: "#000",
      icon: XIcon,
      url: `https://twitter.com/intent/tweet?text=${enc(caption)}&url=${enc(shareUrl)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      color: "#0A66C2",
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
    },
    {
      key: "reddit",
      label: "Reddit",
      color: "#FF4500",
      icon: RedditIcon,
      url: `https://www.reddit.com/submit?url=${enc(shareUrl)}&title=${enc(caption)}`,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      color: "#E60023",
      icon: PinterestIcon,
      url: `https://www.pinterest.com/pin/create/button/?url=${enc(shareUrl)}&description=${enc(caption)}${ogImageUrl ? `&media=${enc(ogImageUrl)}` : ""}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      color: "#25D366",
      icon: WhatsAppIcon,
      url: `https://wa.me/?text=${enc(`${caption}\n${shareUrl}`)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      color: "#229ED9",
      icon: Send,
      url: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(caption)}`,
    },
    {
      key: "messenger",
      label: "Messenger",
      color: "#0084FF",
      icon: MessageSquare,
      url: `https://www.facebook.com/dialog/send?link=${enc(shareUrl)}&app_id=140586622674265&redirect_uri=${enc(shareUrl)}`,
    },
    {
      key: "email",
      label: "Email",
      color: "#94A3B8",
      icon: Mail,
      url: `mailto:?subject=${enc(`${bizName} on LocalBest.ai`)}&body=${enc(`${caption}\n\n${shareUrl}`)}`,
    },
    {
      key: "sms",
      label: "SMS",
      color: "#10B981",
      icon: MessageSquare,
      url: `sms:?&body=${enc(`${caption} ${shareUrl}`)}`,
    },
  ];

  return (
    <div className="space-y-6" data-testid="social-share-section">
      {/* === Verified Badge share card === */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="verified-badge-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Your Verified Badge Share Card</h3>
            <p className="text-xs text-[#94A3B8] mt-0.5">Auto-generated with your business name on the gold LocalBest Verified Badge™. Use this image when posting your listing to social media.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          <div className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-center justify-center">
            {ogImageUrl ? (
              <img src={ogImageUrl} alt={`${bizName} — LocalBest Verified Share Card`} className="max-w-full h-auto rounded-md" data-testid="og-badge-preview" />
            ) : (
              <div className="aspect-[1200/630] w-full bg-white/5 rounded animate-pulse" />
            )}
          </div>
          <div className="space-y-2">
            <a
              href={ogDownloadUrl}
              download={`localbest-verified-${slug}.png`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-[#7C3AED] hover:bg-[#8B5CF6] text-white transition"
              data-testid="download-og-card-btn"
            >
              <Download className="w-4 h-4" /> Download Card (1200×630)
            </a>
            <a
              href={squareDownloadUrl}
              download={`localbest-verified-${slug}-square.png`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium border border-white/15 bg-white/5 hover:bg-white/10 text-white transition"
              data-testid="download-square-card-btn"
            >
              <Download className="w-4 h-4" /> Download Square (Instagram / TikTok)
            </a>
            <Button
              onClick={() => copy(caption, "Caption")}
              variant="outline"
              className="w-full border-white/15 bg-white/5 hover:bg-white/10 text-white"
              data-testid="copy-caption-btn"
            >
              {copied === "Caption" ? <Check className="w-4 h-4 mr-2 text-[#10B981]" /> : <Copy className="w-4 h-4 mr-2" />} Copy Caption
            </Button>
            <p className="text-xs text-[#64748B] leading-relaxed mt-2 italic">
              “{caption}”
            </p>
          </div>
        </div>
      </div>

      {/* === Share whole listing to social === */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="share-whole-listing-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center flex-shrink-0">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Share Your Whole Listing</h3>
            <p className="text-xs text-[#94A3B8] mt-0.5">One-click post your full LocalBest.ai listing to any social platform — caption and link pre-filled. Every click is tracked.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {shareTargets.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.key}
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 px-3 py-2.5 flex items-center gap-2 transition"
                data-testid={`share-${t.key}-btn`}
                title={`Share to ${t.label}`}
              >
                <span className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${t.color}22`, color: t.color }}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="text-white text-xs font-medium truncate">{t.label}</span>
              </a>
            );
          })}
        </div>

        {/* Instagram + TikTok don't allow direct web-intent share — guide owner to use image + caption */}
        <div className="mt-5 rounded-lg bg-gradient-to-r from-[#EC4899]/10 to-[#7C3AED]/10 border border-[#EC4899]/20 p-4" data-testid="ig-tiktok-card">
          <div className="flex items-start gap-3">
            <div className="flex -space-x-2 flex-shrink-0">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#EC4899] to-[#F59E0B] text-white border-2 border-[#0F172A]"><Instagram className="w-4 h-4" /></span>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-black text-white border-2 border-[#0F172A]"><TikTokIcon className="w-4 h-4" /></span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-white font-medium">Posting to Instagram or TikTok?</p>
              <p className="text-xs text-[#94A3B8] mt-0.5">Instagram & TikTok don't support 1-click web sharing. Download the square image + copy the caption, then paste them into the app.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <a
                  href={squareDownloadUrl}
                  download={`localbest-${slug}-ig-tiktok.png`}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-[#EC4899] to-[#F59E0B] text-white hover:opacity-90 transition"
                  data-testid="download-ig-image-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download IG/TikTok Image
                </a>
                <Button size="sm" variant="outline" onClick={() => copy(fullPost, "Full post")} className="border-white/15 bg-white/5 hover:bg-white/10 text-white" data-testid="copy-full-post-btn">
                  {copied === "Full post" ? <Check className="w-3.5 h-3.5 mr-1.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />} Copy Caption + Link
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Inline brand SVG icons (lucide-react doesn't ship X/Reddit/Pinterest/WhatsApp/TikTok logos) */
function XIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2H21l-6.52 7.45L22 22h-6.75l-4.85-6.18L4.8 22H2.04l6.98-7.97L2 2h6.83l4.4 5.78L18.244 2zm-1.18 18.4h1.5L7.05 3.52H5.45l11.614 16.88z" />
    </svg>
  );
}
function RedditIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M14.7 13.5a1.1 1.1 0 1 1 1.1-1.1c0 .61-.49 1.1-1.1 1.1zm-5.4 0a1.1 1.1 0 1 1 1.1-1.1c0 .61-.49 1.1-1.1 1.1zm6.9 2.18c-.78.78-2.27 1.32-4.2 1.32s-3.42-.54-4.2-1.32a.5.5 0 0 1 .7-.7c.59.59 1.83 1.02 3.5 1.02s2.91-.43 3.5-1.02a.5.5 0 1 1 .7.7zM22 12c0-1.1-.9-2-2-2-.55 0-1.04.22-1.4.58-1.36-.89-3.16-1.45-5.13-1.55l1-4.66 3.22.7c0 .8.65 1.43 1.43 1.43s1.43-.65 1.43-1.43-.65-1.43-1.43-1.43c-.56 0-1.04.32-1.27.8l-3.6-.78a.36.36 0 0 0-.43.27l-1.1 5.1c-1.96.1-3.72.66-5.07 1.54A1.99 1.99 0 0 0 2 12c0 .81.49 1.5 1.18 1.81-.04.21-.06.43-.06.65 0 3.07 3.58 5.55 8 5.55s8-2.48 8-5.55c0-.22-.02-.43-.06-.64.7-.31 1.19-1 1.19-1.82z" />
    </svg>
  );
}
function PinterestIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 4.87 3.03 9.04 7.31 10.71-.1-.91-.19-2.3.04-3.29.21-.89 1.36-5.66 1.36-5.66s-.35-.7-.35-1.73c0-1.62.94-2.83 2.11-2.83.99 0 1.47.74 1.47 1.63 0 .99-.63 2.48-.96 3.86-.27 1.16.58 2.1 1.72 2.1 2.07 0 3.66-2.18 3.66-5.32 0-2.78-2-4.72-4.86-4.72-3.31 0-5.25 2.48-5.25 5.05 0 1 .38 2.07.86 2.66.1.12.11.22.08.34-.09.36-.29 1.16-.33 1.32-.05.21-.17.26-.39.16-1.46-.68-2.37-2.81-2.37-4.52 0-3.68 2.67-7.06 7.71-7.06 4.05 0 7.19 2.89 7.19 6.74 0 4.02-2.54 7.26-6.06 7.26-1.18 0-2.29-.61-2.67-1.34l-.73 2.78c-.26 1-.97 2.26-1.44 3.02C9.61 23.2 10.78 23.5 12 23.5c6.35 0 11.5-5.15 11.5-11.5S18.35.5 12 .5z" />
    </svg>
  );
}
function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.94 11.94 0 0 0 12.07 0C5.5 0 .15 5.34.15 11.92c0 2.1.55 4.15 1.6 5.96L0 24l6.27-1.64a11.94 11.94 0 0 0 5.8 1.48h.01c6.57 0 11.92-5.34 11.92-11.92 0-3.18-1.24-6.17-3.48-8.44zM12.08 21.8h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.22-3.72.97 1-3.63-.24-.37a9.85 9.85 0 0 1-1.52-5.25c0-5.46 4.45-9.91 9.91-9.91 2.65 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.46-4.45 9.91-9.91 9.91zm5.43-7.43c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.26-.47-2.4-1.48-.88-.78-1.48-1.74-1.65-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.21-.24-.58-.48-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z" />
    </svg>
  );
}
function TikTokIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.78 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.82-.31z" />
    </svg>
  );
}
